import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { translatePostgrestError } from '../lib/translatePostgrestError';
import { useApp } from './useApp';

/** Joined product preview shape on a staged PO row. */
interface POStagingProductPreview {
  id?: string;
  title?: string | null;
  brand?: string | null;
  // U10 A1 — junction-assembled at the fetch (primary product_distributors row →
  // cost_usd); the products.cost_usd read-copy select is out (slice-B drop family).
  cost_usd?: number | null;
  product_distributors?: { cost_usd: number | null; is_primary: boolean | null }[] | null;
  upc?: string | null;
  asin?: string | null;
  image_url?: string | null;
  // W4a (Phase Wellspring) — the PO-submit verification gate (Moment 2) reads these off the
  // staged product: gate-requiring iff match_resolved_at IS NOT NULL AND match_reviewed = false
  // (the EXACT submit_purchase_order server predicate). Drive the grid match-status column too.
  match_reviewed?: boolean | null;
  match_resolved_at?: string | null;
}

/** Full po_staging row as selected by this hook (incl. joined products preview). */
export interface POStagingItem {
  id: string;
  product_id: string | null;
  isg_sku: string | null;
  quantity: number | null;
  fulfillment_path: string | null;
  cost_per_unit: number | null;
  /**
   * Day 81 P5.6 — distributor active in the session when this row was
   * staged. NULL for pre-Day-81 rows backfilled where products.distributor_id
   * was also NULL. F51 (POTray badge) fires on NOT NULL mismatch with
   * current session's distributor_id. ADR Decision 30 stands — this is
   * captured-context-on-row metadata, NOT scope-of-row.
   */
  distributor_id: string | null;
  /**
   * Per-path unit allocation: { amazon_fba: 3, amazon_fbm: 5, shopify: 2 }.
   * NULL when the caller didn't set per-path quantities (legacy + multi-add
   * flows). Keys use the canonical fulfillment_path long form matching
   * public.marketplace (the stored enum — slice-B family; fpc is the successor
   * config home) + fulfillment_path_config.path_key (Day 60.5 canonicalization).
   */
  allocations: Record<string, number> | null;
  added_at: string | null;
  products?: POStagingProductPreview | null;
}

interface AddPOStagingArgs {
  productId: string;
  isgSku: string | null | undefined;
  costPerUnit: number;
  quantity?: number;
  /**
   * Day 81 P5.6 (F51 + F52) — distributor active in the session when this
   * row is staged. Required at the type level so consumers can't silently
   * omit it. Substrate for the POTray cross-distributor badge AND the
   * calculate_po_impact RPC cross-distributor filter.
   */
  distributorId: string;
  /**
   * Day 81 P5.6 (F53) — required + no hook-level default. Consumers MUST
   * gate on activePaths.length > 0 before calling and pass an explicit
   * path. Schema lockdown migration (Phase 3) also drops the DEFAULT
   * 'amazon_fba' on the column so no SQL-direct path bypasses this gate.
   */
  fulfillmentPath: string;
  /** Optional per-path allocation map. See POStagingItem.allocations. */
  allocations?: Record<string, number> | null;
}

/**
 * Result shape returned by addItem.
 *
 * Day 64 F49 fix: addItem now reports per-call success/failure so callers
 * (handleConfirmMultiAdd partial-failure aggregation, handleAddToPO single-call
 * toast) can surface honest counts instead of always claiming success.
 *
 * - success: false on Supabase upsert error or when user is not authenticated
 * - productId: echoed back for caller-side filtering (`results.filter(...).map(r => r.productId)`)
 * - error?: optional Supabase error message for diagnostic logging; callers
 *   may ignore for user-facing toasts
 */
export interface AddPOStagingResult {
  success: boolean;
  productId: string;
  error?: string;
}

export interface UsePOStagingResult {
  items: POStagingItem[];
  loading: boolean;
  addItem: (args: AddPOStagingArgs) => Promise<AddPOStagingResult>;
  /**
   * Bulk insert/upsert a batch of items in a single round-trip. Used by the
   * Day 59 Restock surface to stage many SKUs in one click without N×
   * fetchItems refreshes. `failed` is binary: 0 on success or items.length
   * on call-level failure (Supabase upsert doesn't surface per-row errors).
   */
  addItems: (items: AddPOStagingArgs[]) => Promise<{ inserted: number; failed: number }>;
  updateQuantity: (stagingId: string, quantity: number) => Promise<void>;
  updateFulfillmentPath: (stagingId: string, fulfillmentPath: string) => Promise<void>;
  updateCost: (stagingId: string, costPerUnit: number) => Promise<void>;
  removeItem: (stagingId: string) => Promise<void>;
  clearAll: () => Promise<void>;
  fetchItems: () => Promise<void>;
  totalSkus: number;
  totalUnits: number;
  totalCost: number;
}

// U10 cost re-point — the junction embed replaces the products.cost_usd
// read-copy select (slice-B drop family); the primary row is picked at the
// fetchItems assembly (mutation .select() returns are discarded — fetchItems
// is the single landing).
const SELECT_COLUMNS = `
  id, product_id, isg_sku, quantity, fulfillment_path, cost_per_unit, distributor_id, allocations, added_at,
  products:product_id ( id, title, brand, upc, asin, image_url, match_reviewed, match_resolved_at,
    product_distributors ( cost_usd, is_primary ) )
`;

/**
 * Persistent PO staging — reads/writes po_staging table in Supabase.
 * Shared between POTray (Buying Session) and POBuilder page.
 *
 * po_staging is deliberately user-scoped, NOT session-scoped. The table's
 * unique constraint is (user_id, product_id) — no session_id column. The
 * staging tray is shared across all of a user's open buying sessions:
 * switching between sessions does not affect staging contents, and items
 * staged from one session are visible from any other open session for the
 * same user. Multi-session isolation (Day 54) applies to filters / weights /
 * constraints / column layout / scenarios via session.ui_state — but NOT
 * to staging. See ARCHITECTURE_DECISIONS.md Decision 30 for full rationale.
 *
 * Day 81 P5.6 — po_staging.distributor_id captures the buying session's
 * distributor at the moment of stage. Forward writes populate this from
 * the active session; pre-Day-81 rows were backfilled from
 * products.distributor_id where available. ADR Decision 30 stands —
 * distributor_id is captured-context-on-row metadata, NOT scope-of-row.
 * The unique constraint remains (user_id, product_id); re-staging the
 * same product in a different distributor's session overwrites the row
 * including its distributor_id. AddPOStagingArgs.distributorId is
 * REQUIRED at the type level; F52 (calculate_po_impact RPC) treats NULL
 * as charity-match in the cross-distributor filter.
 */
export function usePOStaging(options?: {
  /**
   * Day 80 P5.5 F48 — optional user-visible error surfacing. Hook can't
   * directly emit toasts (it has no UI surface), so consumer wires the
   * callback to its local setToast. Optional + back-compat with prior
   * parameterless callers.
   */
  onError?: (message: string) => void;
}): UsePOStagingResult {
  const { user } = useApp();
  const [items, setItems] = useState<POStagingItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const onError = options?.onError;

  // ── Fetch on mount ──────────────────────────────────────────
  const fetchItems = useCallback(async (): Promise<void> => {
    if (!user) {
      setItems([]);
      setLoading(false);
      return;
    }
    const { data, error } = await supabase
      .from('po_staging')
      .select(SELECT_COLUMNS)
      .eq('user_id', user.id)
      .order('added_at', { ascending: true });

    if (error) {
      console.error('[usePOStaging] fetch error:', error.message);
      onError?.('Could not load PO staging. Try again.');
    } else {
      const rows = (data as unknown as POStagingItem[]) || [];
      // Junction-assemble the effective cost (primary row → cost_usd; no
      // retain-last-known — a product with no primary junction row reads null).
      for (const r of rows) {
        if (r.products) {
          r.products.cost_usd =
            r.products.product_distributors?.find((d) => d.is_primary)?.cost_usd ?? null;
        }
      }
      setItems(rows);
    }
    setLoading(false);
  }, [user, onError]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // ── Add product (upsert on user_id + product_id) ────────────
  // Day 64 F49: returns { success, productId, error? } so callers can
  // aggregate partial-failure outcomes (BuyingSession.tsx multi-add) or
  // surface an honest toast on single-call failure (handleAddToPO).
  const addItem = useCallback(
    async ({
      productId,
      isgSku,
      costPerUnit,
      quantity = 1,
      distributorId,
      fulfillmentPath,
      allocations = null,
    }: AddPOStagingArgs): Promise<AddPOStagingResult> => {
      if (!user) {
        return { success: false, productId, error: 'no authenticated user' };
      }
      const { error } = await supabase
        .from('po_staging')
        .upsert(
          {
            user_id: user.id,
            product_id: productId,
            isg_sku: isgSku,
            quantity,
            fulfillment_path: fulfillmentPath,
            cost_per_unit: costPerUnit,
            distributor_id: distributorId,
            // Explicit null on UPSERT clears any prior allocation when a
            // caller restages the same SKU without per-path intent.
            allocations,
          } as never,
          { onConflict: 'user_id,product_id' }
        )
        .select(SELECT_COLUMNS);
      if (error) {
        console.error('[usePOStaging] add error:', error.message);
        onError?.('Could not add product to PO. Try again.');
        return { success: false, productId, error: error.message };
      }
      // Refresh to get joined data
      await fetchItems();
      return { success: true, productId };
    },
    [user, fetchItems, onError]
  );

  // ── Bulk add (batch upsert in a single round-trip) ──────────
  const addItems = useCallback(
    async (itemsToAdd: AddPOStagingArgs[]): Promise<{ inserted: number; failed: number }> => {
      if (!user) return { inserted: 0, failed: itemsToAdd.length };
      if (itemsToAdd.length === 0) return { inserted: 0, failed: 0 };
      const rows = itemsToAdd.map((it) => ({
        user_id: user.id,
        product_id: it.productId,
        isg_sku: it.isgSku,
        quantity: it.quantity ?? 1,
        fulfillment_path: it.fulfillmentPath,
        cost_per_unit: it.costPerUnit,
        distributor_id: it.distributorId,
        // Mirrors addItem semantics — explicit null clears prior allocation.
        allocations: it.allocations ?? null,
      }));
      const { error } = await supabase
        .from('po_staging')
        .upsert(rows as never, { onConflict: 'user_id,product_id' })
        .select(SELECT_COLUMNS);
      if (error) {
        console.error('[usePOStaging] addItems error:', error.message);
        onError?.('Could not add products to PO. Try again.');
        return { inserted: 0, failed: itemsToAdd.length };
      }
      await fetchItems();
      return { inserted: itemsToAdd.length, failed: 0 };
    },
    [user, fetchItems, onError]
  );

  // ── Update quantity ─────────────────────────────────────────
  const updateQuantity = useCallback(
    async (stagingId: string, quantity: number): Promise<void> => {
      if (quantity < 1) return;
      const { error } = await supabase
        .from('po_staging')
        .update({ quantity } as never)
        .eq('id', stagingId);
      if (error) {
        console.error('[usePOStaging] update qty error:', error.message);
        onError?.('Could not update quantity. Try again.');
        return;
      }
      setItems((prev) =>
        prev.map((item) => (item.id === stagingId ? { ...item, quantity } : item))
      );
    },
    [onError]
  );

  // ── Update fulfillment path ─────────────────────────────────
  const updateFulfillmentPath = useCallback(
    async (stagingId: string, fulfillmentPath: string): Promise<void> => {
      const { error } = await supabase
        .from('po_staging')
        .update({ fulfillment_path: fulfillmentPath } as never)
        .eq('id', stagingId);
      if (error) {
        console.error('[usePOStaging] update path error:', error.message);
        onError?.('Could not update fulfillment path. Try again.');
        return;
      }
      setItems((prev) =>
        prev.map((item) =>
          item.id === stagingId ? { ...item, fulfillment_path: fulfillmentPath } : item
        )
      );
    },
    [onError]
  );

  // ── Update cost per unit ────────────────────────────────────
  const updateCost = useCallback(
    async (stagingId: string, costPerUnit: number): Promise<void> => {
      const { error } = await supabase
        .from('po_staging')
        .update({ cost_per_unit: costPerUnit } as never)
        .eq('id', stagingId);
      if (error) {
        console.error('[usePOStaging] update cost error:', error.message);
        onError?.('Could not update cost. Try again.');
        return;
      }
      setItems((prev) =>
        prev.map((item) => (item.id === stagingId ? { ...item, cost_per_unit: costPerUnit } : item))
      );
    },
    [onError]
  );

  // ── Remove single item ──────────────────────────────────────
  const removeItem = useCallback(
    async (stagingId: string): Promise<void> => {
      const { error } = await supabase.from('po_staging').delete().eq('id', stagingId);
      if (error) {
        const ue = translatePostgrestError(error);
        console.error('[usePOStaging] remove error:', ue.message);
        onError?.(ue.message);
        return;
      }
      setItems((prev) => prev.filter((item) => item.id !== stagingId));
    },
    [onError]
  );

  // ── Clear all (after PO submission) ─────────────────────────
  const clearAll = useCallback(async (): Promise<void> => {
    if (!user) return;
    const { error } = await supabase.from('po_staging').delete().eq('user_id', user.id);
    if (error) {
      const ue = translatePostgrestError(error);
      console.error('[usePOStaging] clear all error:', ue.message);
      onError?.(ue.message);
      return;
    }
    setItems([]);
  }, [user, onError]);

  // ── Computed summaries ──────────────────────────────────────
  const totalSkus = items.length;
  const totalUnits = items.reduce((s, item) => s + (item.quantity || 0), 0);
  const totalCost = items.reduce(
    (s, item) =>
      s +
      (item.quantity || 0) * (Number(item.cost_per_unit) || Number(item.products?.cost_usd) || 0),
    0
  );

  return {
    items,
    loading,
    addItem,
    addItems,
    updateQuantity,
    updateFulfillmentPath,
    updateCost,
    removeItem,
    clearAll,
    fetchItems,
    totalSkus,
    totalUnits,
    totalCost,
  };
}
